import {
  CSSProperties,
  ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { defaultTheme } from "../../domains/app/contexts/theme.context";

interface ScrollableLayoutProps {
  children: ReactNode;
  className?: string;
  containerClassName?: string;
  containerStyle?: CSSProperties;
  style?: CSSProperties;
  onScroll?: (event: React.UIEvent<HTMLDivElement>) => void;
  onScrollEnd?: (event: React.UIEvent<HTMLDivElement>) => void;
  // `React.Ref` (not just `RefObject`) so callers can pass a callback ref —
  // e.g. the `use-stick-to-bottom` scroll ref for streaming auto-scroll.
  scrollRef?: React.Ref<HTMLDivElement>;
  hideScrollbar?: boolean;
  background?: string;
  header?: ReactNode;
  headerBackground?: string;
  headerClassName?: string;
  headerStyle?: CSSProperties;
  footer?: ReactNode;
}

export default function ScrollableLayout({
  children,
  className,
  containerClassName,
  containerStyle,
  style,
  onScroll,
  onScrollEnd,
  scrollRef,
  hideScrollbar = false,
  background,
  header,
  headerBackground,
  headerClassName,
  headerStyle: headerStyleProp,
  footer,
}: ScrollableLayoutProps) {
  const internalRef = useRef<HTMLDivElement>(null);
  const ref = scrollRef || internalRef;
  const headerRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [footerHeight, setFooterHeight] = useState(0);
  const hasHeader = header !== undefined && header !== null;
  const hasFooter = footer !== undefined && footer !== null;

  function updateHeaderHeight(nextHeight: number) {
    setHeaderHeight((currentHeight) => {
      return currentHeight === nextHeight ? currentHeight : nextHeight;
    });
  }

  function updateFooterHeight(nextHeight: number) {
    setFooterHeight((currentHeight) => {
      return currentHeight === nextHeight ? currentHeight : nextHeight;
    });
  }

  // Measure header and footer heights
  useLayoutEffect(() => {
    if (headerRef.current) {
      const height = headerRef.current.offsetHeight;
      updateHeaderHeight(height);
    } else {
      updateHeaderHeight(0);
    }
    if (footerRef.current) {
      const height = footerRef.current.offsetHeight;
      updateFooterHeight(height);
    } else {
      updateFooterHeight(0);
    }
  }, [hasHeader, hasFooter]);

  // Use ResizeObserver to handle dynamic height changes
  useEffect(() => {
    if (!headerRef.current && !footerRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === headerRef.current) {
          updateHeaderHeight(entry.target.clientHeight);
        }
        if (entry.target === footerRef.current) {
          updateFooterHeight(entry.target.clientHeight);
        }
      }
    });

    if (headerRef.current) {
      resizeObserver.observe(headerRef.current);
    }
    if (footerRef.current) {
      resizeObserver.observe(footerRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [hasHeader, hasFooter]);

  // Container classes - fixed positioned flex column with overflow hidden
  // Desktop: constrain to center column (600px)
  const containerBaseClasses = `box-border flex flex-col fixed top-0 left-0 w-full h-dvh overflow-y-hidden`;
  const containerClasses = [containerBaseClasses, containerClassName]
    .filter(Boolean)
    .join(" ");

  // Scrollable content area classes - always vertical scrolling
  const scrollableBaseClasses =
    "box-border flex-1 min-h-100dvh overflow-x-hidden overflow-y-auto";

  const scrollbarClasses = hideScrollbar
    ? "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
    : "";

  const scrollableClasses = [scrollableBaseClasses, scrollbarClasses, className]
    .filter(Boolean)
    .join(" ");

  const scollablePaddingTop = hasHeader
    ? `${headerHeight}px`
    : "var(--safe-area-top)";

  // The measured header height, safe-area included, as a CSS custom property
  // for `fixed` descendants that must hang under the header (the JuisciGPT
  // tabs pill): `fixed` escapes the layout flow but not the cascade.
  const rootStyle: CSSProperties & Record<`--${string}`, string> = {
    background: defaultTheme.colors.background,
    "--scrollable-header-height": `${headerHeight}px`,
    ...containerStyle,
  };

  return (
    <div className={containerClasses} style={rootStyle}>
      {hasHeader && (
        <div
          ref={headerRef}
          className={`absolute top-0 left-0 w-full z-20 ${headerClassName ?? ""}`}
          style={{
            paddingTop: "var(--safe-area-top)",
            ...(!headerClassName && {
              background: headerBackground ?? "white",
            }),
            ...headerStyleProp,
          }}
        >
          {header}
        </div>
      )}
      <div
        ref={ref}
        className={scrollableClasses}
        style={{
          paddingTop: scollablePaddingTop,
          paddingBottom: `${footerHeight}px`,
          background: background || defaultTheme.colors.background,
          ...style,
        }}
        onScroll={(e) => {
          onScroll?.(e);
          const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
          const scrollRate = (scrollTop + clientHeight) / scrollHeight;
          if (scrollRate >= 0.9) onScrollEnd?.(e);
        }}
      >
        {children}
      </div>
      {hasFooter && (
        <div
          ref={footerRef}
          className="absolute bottom-0 left-0 w-full z-10 bg-white"
          style={{
            paddingBottom: "calc(var(--safe-area-bottom) / 2)",
          }}
        >
          {footer}
        </div>
      )}
    </div>
  );
}
